import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useRequest from "./components/hook/use-req";

// Public, unauthenticated page reached from an email link:
// {FE_PORTAL_URI}/subscription?o={org_id}&c={contact_id}&ch={channel}&t={token}
// Every request just forwards those same four query params straight through
// to the backend (o/c/ch/t), matching:
//   POST /public/subscriptions/unsubscribe?o=...&c=...&ch=...&t=...
//   POST /public/subscriptions/resubscribe?o=...&c=...&ch=...&t=...
// There's no "status" call and no per-category preferences endpoint yet, so
// this only handles the channel-level unsubscribe/resubscribe flow — no
// fabricated category toggles.

type ViewState = "confirm" | "unsubscribed" | "resubscribed" | "cancelled" | "invalid";

const CheckCircleIcon = () => (
    <div className="w-14 h-14 rounded-full bg-emerald-200 flex items-center justify-center">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="#064e3b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </div>
);

const MailIcon = () => (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" className="text-gray-900">
        <path d="M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const channelLabel = (ch: string): string => {
    switch (ch.toLowerCase()) {
        case "sms": return "SMS messages";
        case "whatsapp": return "WhatsApp messages";
        case "rcs": return "RCS messages";
        case "voice": return "calls";
        case "email":
        default: return "emails";
    }
};

const Toggle: React.FC<{ checked: boolean; onChange: (checked: boolean) => void }> = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full shrink-0 cursor-pointer transition-colors ${checked ? "border-none" : "bg-white border border-gray-300"}`}
        style={checked ? { backgroundColor: "#86EFAC" } : undefined}
    >
        <span
            className={`absolute top-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`}
        >
            {checked && <span className="w-2 h-2 rounded-full bg-gray-900" />}
        </span>
    </button>
);

// Field names are a best-effort guess (snake_case matching the visible
// labels) — no per-category request shape has been confirmed by backend yet.
const CATEGORIES: Array<{ key: "newsletters" | "product_updates" | "promotional_offers"; label: string; description: string }> = [
    { key: "newsletters", label: "Newsletters", description: "Product news, tips, and updates from our team." },
    { key: "product_updates", label: "Product Updates", description: "New features, improvements, and releases." },
    { key: "promotional_offers", label: "Promotional Offers", description: "Discounts, deals, and special promotions." },
];

const Subscription = () => {
    const [searchParams] = useSearchParams();
    const o = searchParams.get("o") || "";
    const c = searchParams.get("c") || "";
    const ch = searchParams.get("ch") || "";
    const t = searchParams.get("t") || "";
    const hasValidParams = !!(o && c && t);

    const [view, setView] = useState<ViewState>(hasValidParams ? "confirm" : "invalid");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // "You are currently subscribed to" — defaults to all-on since there's no
    // status endpoint to read real per-category state from yet.
    const [categoryState, setCategoryState] = useState<Record<string, boolean>>({
        newsletters: true,
        product_updates: true,
        promotional_offers: true,
    });

    const { makeRequest: unsubscribeRequest } = useRequest("/public/subscriptions/unsubscribe", "POST");
    const { makeRequest: resubscribeRequest } = useRequest("/public/subscriptions/resubscribe", "POST");

    const linkParams = { o, c, ch, t };

    const handleUnsubscribe = async () => {
        setSubmitting(true);
        setError(null);
        const [response, status] = await unsubscribeRequest(categoryState, linkParams);
        setSubmitting(false);
        if (status >= 200 && status < 300) {
            setView("unsubscribed");
        } else {
            setError(response?.detail || response?.message || "Something went wrong. Please try again.");
        }
    };

    const handleResubscribe = async () => {
        setSubmitting(true);
        setError(null);
        const [response, status] = await resubscribeRequest(
            { newsletters: true, product_updates: true, promotional_offers: true },
            linkParams,
        );
        setSubmitting(false);
        if (status >= 200 && status < 300) {
            setView("resubscribed");
        } else {
            setError(response?.detail || response?.message || "Something went wrong. Please try again.");
        }
    };

    const label = channelLabel(ch);
    const activeCategoryLabels = CATEGORIES.filter((cat) => categoryState[cat.key]).map((cat) => cat.label);

    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(135deg, #e9e7fb 0%, #eaf4fb 100%)" }}>
            <div className="bg-white rounded-3xl shadow-xl max-w-lg w-full p-10 text-center">
                {view === "invalid" && (
                    <>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">This link isn't valid.</h1>
                        <p className="text-gray-500">
                            This subscription link is missing or has expired. Please use the link from your most recent email.
                        </p>
                    </>
                )}

                {view === "confirm" && (
                    <>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Are you sure you want to unsubscribe?</h1>
                        <p className="text-gray-500 mb-6">
                            Once you unsubscribe, you'll stop receiving {label} from us.
                        </p>
                        <div className="flex justify-center mb-6">
                            <MailIcon />
                        </div>

                        <h2 className="text-base font-semibold text-gray-900 mb-3 text-left">You are currently subscribed to:</h2>
                        <div className="space-y-2 mb-4">
                            {CATEGORIES.map((cat) => (
                                <div key={cat.key} className="flex items-center justify-between gap-3 bg-[#FBF9F0] rounded-xl px-4 py-3 text-left">
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900">{cat.label}</p>
                                        <p className="text-xs text-gray-500">{cat.description}</p>
                                    </div>
                                    <Toggle
                                        checked={categoryState[cat.key]}
                                        onChange={(checked) => setCategoryState((prev) => ({ ...prev, [cat.key]: checked }))}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="bg-indigo-50/60 rounded-xl px-4 py-3 text-left mb-6">
                            <p className="text-sm font-semibold text-gray-900 mb-0.5">You'll miss out on:</p>
                            <p className="text-xs text-gray-500">
                                {activeCategoryLabels.length > 0
                                    ? activeCategoryLabels.join(", ")
                                    : "You've already turned everything off above."}
                            </p>
                        </div>

                        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
                        <button
                            onClick={handleUnsubscribe}
                            disabled={submitting}
                            className="w-full py-3 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mb-3"
                        >
                            {submitting ? "Unsubscribing…" : "Yes, Unsubscribe"}
                        </button>
                        <button
                            onClick={() => setView("cancelled")}
                            disabled={submitting}
                            className="text-sm font-medium underline text-[#4338CA] hover:text-indigo-700 bg-transparent border-none cursor-pointer"
                        >
                            No, keep me subscribed
                        </button>
                    </>
                )}

                {view === "cancelled" && (
                    <>
                        <div className="flex justify-center mb-4">
                            <CheckCircleIcon />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">You're all set.</h1>
                        <p className="text-gray-500">No changes were made — you're still subscribed to {label}.</p>
                    </>
                )}

                {view === "unsubscribed" && (
                    <>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">You've been unsubscribed.</h1>
                        <p className="text-gray-500 mb-1">You'll no longer receive {label} from us.</p>
                        <p className="text-gray-500 mb-6">We're sorry to see you go.</p>
                        <div className="flex justify-center mb-8">
                            <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <mask id="path-1-outside-1_3161_900" maskUnits="userSpaceOnUse" x="0" y="0" width="66" height="66" fill="black">
                                    <rect fill="white" width="66" height="66" />
                                    <path d="M1 33C1 15.3269 15.3269 1 33 1C50.6731 1 65 15.3269 65 33C65 50.6731 50.6731 65 33 65C15.3269 65 1 50.6731 1 33Z" />
                                </mask>
                                <path d="M1 33C1 15.3269 15.3269 1 33 1C50.6731 1 65 15.3269 65 33C65 50.6731 50.6731 65 33 65C15.3269 65 1 50.6731 1 33Z" fill="#86EFAC" />
                                <path d="M1 33M65 33M65 33M1 33M33 1M65 33M33 65M1 33M33 65V64C15.8792 64 2 50.1208 2 33H1H0C0 51.2254 14.7746 66 33 66V65ZM65 33H64C64 50.1208 50.1208 64 33 64V65V66C51.2254 66 66 51.2254 66 33H65ZM33 1V2C50.1208 2 64 15.8792 64 33H65H66C66 14.7746 51.2254 0 33 0V1ZM33 1V0C14.7746 0 0 14.7746 0 33H1H2C2 15.8792 15.8792 2 33 2V1Z" fill="#3A3428" mask="url(#path-1-outside-1_3161_900)" />
                                <path d="M46.333 24.666L29.6663 41.3327L21.333 32.9993" stroke="#3A3428" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>

                        </div>
                        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
                        <div className="bg-[#FBF9F0] rounded-2xl p-5 text-left">
                            <h2 className="text-sm font-semibold text-gray-900 mb-1">Changed your mind?</h2>
                            <p className="text-xs text-gray-500 mb-3">If you change your mind, you can subscribe again anytime and choose what you’d like</p>
                            <button
                                onClick={handleResubscribe}
                                 disabled={submitting}
                                className="py-2.5 px-5 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? "Subscribing…" : "Subscribe again"}
                            </button>
                        </div>
                    </>
                )}

                {view === "resubscribed" && (
                    <>
                        <div className="flex justify-center mb-4">
                            <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <mask id="path-1-outside-1_3161_900" maskUnits="userSpaceOnUse" x="0" y="0" width="66" height="66" fill="black">
                                    <rect fill="white" width="66" height="66" />
                                    <path d="M1 33C1 15.3269 15.3269 1 33 1C50.6731 1 65 15.3269 65 33C65 50.6731 50.6731 65 33 65C15.3269 65 1 50.6731 1 33Z" />
                                </mask>
                                <path d="M1 33C1 15.3269 15.3269 1 33 1C50.6731 1 65 15.3269 65 33C65 50.6731 50.6731 65 33 65C15.3269 65 1 50.6731 1 33Z" fill="#86EFAC" />
                                <path d="M1 33M65 33M65 33M1 33M33 1M65 33M33 65M1 33M33 65V64C15.8792 64 2 50.1208 2 33H1H0C0 51.2254 14.7746 66 33 66V65ZM65 33H64C64 50.1208 50.1208 64 33 64V65V66C51.2254 66 66 51.2254 66 33H65ZM33 1V2C50.1208 2 64 15.8792 64 33H65H66C66 14.7746 51.2254 0 33 0V1ZM33 1V0C14.7746 0 0 14.7746 0 33H1H2C2 15.8792 15.8792 2 33 2V1Z" fill="#3A3428" mask="url(#path-1-outside-1_3161_900)" />
                                <path d="M46.333 24.666L29.6663 41.3327L21.333 32.9993" stroke="#3A3428" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">You're subscribed again.</h1>
                        <p className="text-gray-500">You'll continue receiving {label} from us.</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default Subscription;
